import React from 'react';
import objeler from '../objeler';
import {
    createBekoSpecTableBinding,
    createImageBinding,
    createScalarBinding,
    createTableBinding,
    findImageCandidatePaths,
    getStructuredJsonSample,
    normalizePathList,
    toFirstItemPath,
    toSafePathOptions
} from '../Functions/structuredJsonBuilder';

const panelStyle = {
    border: '1px solid #d0d7de',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '15px',
    backgroundColor: '#f8fafc'
};

const labelStyle = {
    display: 'block',
    marginTop: '8px',
    marginBottom: '4px',
    fontWeight: 'bold'
};

const inputStyle = {
    width: '100%',
    padding: '5px',
    fontSize: '12px'
};

const buttonStyle = {
    marginTop: '8px',
    marginRight: '6px',
    padding: '6px 10px'
};

const statusStyle = {
    marginTop: '8px',
    padding: '6px 8px',
    backgroundColor: '#eef6ff',
    border: '1px solid #b6d7f5',
    borderRadius: '4px',
    color: '#1f4e79',
    fontSize: '12px'
};

class StructuredBindingPanel extends React.Component {
    getSample() {
        return getStructuredJsonSample(objeler.ModalDocParameters);
    }

    getBinding() {
        return this.props.binding || {};
    }

    applyBinding(binding) {
        if (this.props.onBindingChange) {
            this.props.onBindingChange(binding);
        }
        this.forceUpdate();
    }

    renderScalar() {
        const sample = this.getSample();
        const binding = this.getBinding();
        const pathOptions = toSafePathOptions(sample.paths);

        return (
            <div>
                <label style={labelStyle}>Path</label>
                <select
                    value={binding.path || ''}
                    onChange={e => this.applyBinding(createScalarBinding(e.target.value))}
                    style={inputStyle}
                >
                    <option value="">-- Select JSON path --</option>
                    {pathOptions.map(path => (
                        <option key={path} value={path}>{path}</option>
                    ))}
                </select>
            </div>
        );
    }

    renderImage() {
        const sample = this.getSample();
        const binding = this.getBinding();
        const pathCandidates = binding.pathCandidates || [];
        const imagePaths = findImageCandidatePaths(toSafePathOptions(sample.paths));

        return (
            <div>
                <button
                    type="button"
                    style={buttonStyle}
                    onClick={() => {
                        const candidates = imagePaths.length > 0
                            ? imagePaths.slice(0, 5)
                            : [
                                '$.products[0].images[0].url',
                                '$.products[0].images[0].originalUrl',
                                '$.products[0].images[0].swogoUrl'
                            ];
                        this.applyBinding(createImageBinding(candidates.map(toFirstItemPath)));
                    }}
                >
                    Apply Beko Product Image Preset
                </button>
                <label style={labelStyle}>Path Candidates</label>
                <textarea
                    value={pathCandidates.join('\n')}
                    onChange={e => this.applyBinding(createImageBinding(e.target.value))}
                    style={{...inputStyle, height: '80px'}}
                />
            </div>
        );
    }

    renderTable() {
        const binding = this.getBinding();
        const packing = binding.packing || {};
        const group = binding.group || {};
        const columns = binding.columns || [];
        const itemsPerVisualRow = packing.itemsPerVisualRow || 2;
        const packingEnabled = packing.enabled !== false;

        return (
            <div>
                <button
                    type="button"
                    style={buttonStyle}
                    onClick={() => this.applyBinding(createBekoSpecTableBinding(itemsPerVisualRow))}
                >
                    Apply Beko Spec Preset
                </button>
                {binding.kind === 'table' && (
                    <div style={statusStyle}>Structured runtime binding configured.</div>
                )}

                <label style={labelStyle}>Rows Path</label>
                <input
                    type="text"
                    value={binding.rowsPath || ''}
                    onChange={e => this.applyBinding(createTableBinding({
                        rowsPath: e.target.value,
                        columns,
                        groupKeyPath: binding.group && binding.group.keyPath,
                        groupLabelTemplate: binding.group && binding.group.labelTemplate,
                        packingEnabled: packing.enabled,
                        itemsPerVisualRow,
                        itemColumns: packing.itemColumns || []
                    }))}
                    style={inputStyle}
                />

                <label style={labelStyle}>Group Key Path</label>
                <input
                    type="text"
                    value={group.keyPath || ''}
                    onChange={e => this.applyBinding(createTableBinding({
                        rowsPath: binding.rowsPath,
                        columns,
                        groupKeyPath: e.target.value,
                        groupLabelTemplate: group.labelTemplate,
                        packingEnabled,
                        itemsPerVisualRow,
                        itemColumns: packing.itemColumns || []
                    }))}
                    style={inputStyle}
                />

                <label style={labelStyle}>Group Label Template</label>
                <input
                    type="text"
                    value={group.labelTemplate || ''}
                    onChange={e => this.applyBinding(createTableBinding({
                        rowsPath: binding.rowsPath,
                        columns,
                        groupKeyPath: group.keyPath,
                        groupLabelTemplate: e.target.value,
                        packingEnabled,
                        itemsPerVisualRow,
                        itemColumns: packing.itemColumns || []
                    }))}
                    style={inputStyle}
                />

                <div style={{marginTop: '10px'}}>
                    <input
                        type="checkbox"
                        id="structuredPackingEnabled"
                        checked={packingEnabled}
                        onChange={e => this.applyBinding({
                            ...binding,
                            packing: {
                                ...packing,
                                enabled: e.target.checked,
                                itemsPerVisualRow,
                                itemColumns: packing.itemColumns || ['name', 'value']
                            }
                        })}
                    />
                    <label htmlFor="structuredPackingEnabled" style={{marginLeft: '5px'}}>
                        Packing Enabled
                    </label>
                </div>

                <label style={labelStyle}>Items Per Visual Row</label>
                <select
                    value={itemsPerVisualRow}
                    onChange={e => this.applyBinding({
                        ...binding,
                        packing: {
                            ...packing,
                            enabled: packingEnabled,
                            itemsPerVisualRow: Number(e.target.value),
                            itemColumns: packing.itemColumns || ['name', 'value']
                        }
                    })}
                    style={inputStyle}
                >
                    {[2, 3, 4, 5].map(value => (
                        <option key={value} value={value}>{value}</option>
                    ))}
                </select>

                <label style={labelStyle}>Columns JSON</label>
                <textarea
                    value={JSON.stringify(columns, null, 2)}
                    onChange={e => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            this.applyBinding({...binding, columns: Array.isArray(parsed) ? parsed : columns});
                        } catch {
                            this.forceUpdate();
                        }
                    }}
                    style={{...inputStyle, height: '90px'}}
                />

                <label style={labelStyle}>Item Columns</label>
                <input
                    type="text"
                    value={(packing.itemColumns || []).join(', ')}
                    onChange={e => this.applyBinding({
                        ...binding,
                        packing: {
                            ...packing,
                            enabled: packingEnabled,
                            itemsPerVisualRow,
                            itemColumns: normalizePathList(String(e.target.value).split(','))
                        }
                    })}
                    style={inputStyle}
                />
            </div>
        );
    }

    render() {
        const sample = this.getSample();
        return (
            <div style={panelStyle}>
                <div style={{fontWeight: 'bold'}}>Structured Binding</div>
                <div style={{fontSize: '12px', color: sample.valid ? '#0f7b0f' : '#666', marginTop: '4px'}}>
                    {sample.valid
                        ? `${sample.fileName} sample loaded`
                        : 'Upload a Structured JSON Sample in Document Preferences for guided path options.'}
                </div>
                {this.props.kind === 'table' && this.renderTable()}
                {this.props.kind === 'image' && this.renderImage()}
                {this.props.kind === 'scalar' && this.renderScalar()}
            </div>
        );
    }
}

export default StructuredBindingPanel;
